import debounce from 'lodash/debounce';
import PropTypes from 'prop-types';
import React, { useState, useRef, useCallback, useEffect } from 'react';

import BackArrow, { ARROW_SIZES } from 'components/BackArrow';
import CloseIcon, { SIZES } from 'components/CloseIcon';
import IconButton from 'components/IconButton';
import Input from 'components/Input';
import Theme from 'styles/theme';
import I18n from 'utils/I18n';

const SearchBarModal = ({ inputValue, onChange, onClear, onBackArrowClick, onKeyDownEnter }) => {
  const [valueState, setValueState] = useState(inputValue);
  const ref = useRef(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedOnChange = useCallback(
    debounce(valueDebounced => {
      onChange(valueDebounced);
    }, 500),
    []
  );

  const onChangeEvent = event => {
    const { value } = event.target;

    setValueState(value);
    debouncedOnChange(value);
  };

  const onClearEvent = () => {
    onClear();

    setValueState('');
  };

  const onInputKeyDown = event => {
    if (event.key === 'Enter') {
      onKeyDownEnter();
    }
  };

  const onFormSubmit = event => {
    event.preventDefault();
    ref.current.blur();
  };

  useEffect(() => {
    setValueState(inputValue);
  }, [inputValue]);

  return (
    <div className="SearchBarModal">
      <form className="SearchBarModal__form" onSubmit={onFormSubmit}>
        <div className="SearchBarModal__container">
          <div className="SearchBarModal__icon-left">
            <BackArrow
              className="SearchBarModal__back-arrow-button"
              onClick={onBackArrowClick}
              size={ARROW_SIZES.MEDIUM}
            />
          </div>
          <div className="SearchBarModal__search_input">
            <Input
              inputType="text"
              id="SearchBarModal__input"
              className="SearchBarModal__input"
              onChange={onChangeEvent}
              ariaLabel={I18n.localize('searchBar.enterSearchTerm')}
              placeholder={I18n.localize('searchBar.enterSearchTerm')}
              value={valueState}
              forwardedRef={ref}
              onKeyDown={onInputKeyDown}
              autoFocus
            />
            <div className="SearchBarModal__icon-right">
              {valueState !== '' && (
                <IconButton
                  className="SearchBarModal__clear-button"
                  onClick={onClearEvent}
                  aria-label={I18n.localize('searchBar.clear')}
                  type="button"
                >
                  <CloseIcon
                    className="SearchBarModal__clear-icon"
                    size={SIZES.SMALL}
                    disableHoverAnimation
                  />
                </IconButton>
              )}
            </div>
          </div>
        </div>
      </form>

      <style jsx>
        {`
          .SearchBarModal__container {
            padding: ${Theme.get('spacing') * 2}px;
            display: flex;
          }
          .SearchBarModal__icon-left {
            margin: auto 0;
            padding-right: ${Theme.get('spacing') * 2}px;
          }
          .SearchBarModal__search_input {
            width: 100%;
            position: relative;
          }
          .SearchBarModal__icon-right {
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            right: ${Theme.get('spacing') * 2}px;
          }
        `}
      </style>
      <style jsx global>
        {`
          .SearchBarModal__input.Input {
            font-weight: ${Theme.get('font-weight-normal')};
            color: ${Theme.get('colors-black-80')};
            padding-left: ${Theme.get('spacing') * 4}px;
            padding-right: ${Theme.get('spacing') * 6}px;
            border: none;
            transition: none;
            border-radius: ${Theme.get('font-size-small')}px;
          }
          .SearchBarModal__input.Input::placeholder {
            color: ${Theme.get('colors-black-60')};
          }
          .SearchBarModal__input.Input:active,
          .SearchBarModal__input.Input:focus {
            box-shadow: none;
          }
          .SearchBarModal .SearchBarModal__clear-icon.CloseIcon:after,
          .SearchBarModal .SearchBarModal__clear-icon.CloseIcon:before {
            background-color: ${Theme.get('colors-black-60')};
            height: 18px;
          }
          .SearchBarModal__back-arrow-button.BackArrow--left {
            background-color: transparent;
          }
          .IconButton.SearchBarModal__clear-button,
          .IconButton.SearchBarModal__clear-button,
          .IconButton.SearchBarModal__back-arrow-button {
            min-height: auto;
            min-width: auto;
            margin: 0;
            padding: ${Theme.get('spacing')}px;
          }
        `}
      </style>
    </div>
  );
};

SearchBarModal.propTypes = {
  inputValue: PropTypes.string,
  onClear: PropTypes.func,
  onChange: PropTypes.func,
  onBackArrowClick: PropTypes.func,
  onKeyDownEnter: PropTypes.func,
};

SearchBarModal.defaultProps = {
  inputValue: '',
  onBackArrowClick: () => {},
  onChange: () => {},
  onClear: () => '',
  onKeyDownEnter: () => {},
};

export default SearchBarModal;
